import * as types from '../mutation-types'
import req from '../../lib/authRequest'
import $ from 'jquery'

/*
 * Vuex module to handle DNS related things
 */

const DEFAULT_NEW_ZONE = {
  name: '',
  ttl: '3600'
}

// Initial state
const state = {
  total_zones: 0,
  zones: [],
  editing_zone: {
    zone_name: '',
    ttl: ''
  },
  active_zone_id: -1,
  new_zone: DEFAULT_NEW_ZONE,
  zone_timers_loaded: false,
  disabled_zones: [],
  creating_alias: false,
  // For paginating zones
  zoneOffset: 0,
  zoneLimit: 10,
  zonePage: 0,
  showAllZones: false,
  records: [],
  total_records: 0,
  recordsOffset: 0,
  recordsLimit: 10,
  recordsPage: 0,
  showAllRecords: false,
  record_types: [
    {
      name: 'A',
      description: 'An IPv4 address, used to map hostnames to an IP address',
      options: [
        {
          name: 'ipv4_address',
          label: 'IP Address',
          type: 'text'
        }
      ]
    },
    {
      name: 'TXT',
      description: 'Arbitrary text in a DNS record',
      options: [
        {
          name: 'text',
          label: 'Text',
          type: 'text'
        }
      ]
    },
    {
      name: 'AAAA',
      description: 'An IPv6 address, used to map hostnames to an IP address',
      options: [
        {
          name: 'ipv6_address',
          label: 'IPv6 Address',
          type: 'text'
        }
      ]
    },
    {
      name: 'CNAME',
      description: 'Alias of one name to another',
      options: [
        {
          name: 'canonical_name',
          label: 'Aliased Domain',
          type: 'text'
        }
      ]
    },
    {
      name: 'SRV',
      description: 'Generalized service location record',
      options: [
        {
          name: 'priority',
          label: 'Priority',
          type: 'number'
        },
        {
          name: 'weight',
          label: 'Weight',
          type: 'number'
        },
        {
          name: 'port',
          label: 'Port',
          type: 'number'
        },
        {
          name: 'target',
          label: 'Target Host',
          type: 'text'
        }
      ]
    },
    {
      name: 'NS',
      description: 'Delegates a DNS zone to use the given authoritative name servers',
      options: [
        {
          name: 'nameserver',
          label: 'Nameserver',
          type: 'text'
        }
      ]
    },
    {
      name: 'MX',
      description: 'Maps a zone name to a list of message transfer agents',
      options: [
        {
          name: 'target',
          label: 'Target',
          type: 'text'
        },
        {
          name: 'priority',
          label: 'Priority',
          type: 'number'
        }
      ]
    }
  ]
}

const getters = {
  active_zone: state => {
    return state.zones.filter(zone => zone.zone_id === state.active_zone_id)[0]
  },
  zonesCount: state => state.zones.length,
  hasZones: (state, getters) => getters.zonesCount > 0,
  getDisabledZoneById: (state) => (id) => {
    return state.disabled_zones.find(zone => zone.zone_id === id)
  },
  getDisabledZoneDeleteText: (state, getters) => (id) => {
    let zone = getters.getDisabledZoneById(id)
    if (zone === undefined) {
      return 'Delete'
    } else {
      return 'Delete (Disabled for ' + zone.last_start.toString() + 's)'
    }
  },
  getDisabledZoneEditText: (state, getters) => (id) => {
    let zone = getters.getDisabledZoneById(id)
    if (zone === undefined) {
      return 'Edit'
    } else {
      return 'Edit (Disabled for ' + zone.last_start.toString() + 's)'
    }
  }
}

const mutations = {
  [types.GET_ZONES] (state, result) {
    state.total_zones = result.total_zones
    state.zones = result.zones
    // Set the selected zone to the first in the list
    if (state.zones !== undefined && state.zones.length > 0 && state.active_zone_id === -1) {
      state.active_zone_id = state.zones[0].zone_id
    }
  },
  [types.SET_DEFAULT_ACTIVE_ZONE] (state) {
    if (state.zones.length > 0) {
      state.active_zone_id = state.zones[0].zone_id
    } else {
      state.active_zone_id = -1
    }
  },
  [types.SET_ACTIVE_ZONE] (state, activeZone) {
    state.active_zone_id = activeZone.zone_id
  },
  [types.NULL_ACTIVE_ZONE] (state) {
    state.active_zone_id = -1
  },
  [types.SET_EDITING_ZONE_ALIASES] (state, { zoneAliases }) {
    state.editing_zone.aliases = zoneAliases
  },
  [types.SET_EDITING_ZONE] (state, zone) {
    state.editing_zone = $.extend(true, {}, zone)
  },
  [types.UPDATE_EDITING_ZONE] (state, { name, value }) {
    state.editing_zone[name] = value
  },
  [types.BEGIN_CREATING_ALIAS] (state) {
    state.creating_alias = true
  },
  [types.END_CREATING_ALIAS] (state) {
    state.creating_alias = false
  },
  [types.UPDATE_NEW_ZONE] (state, { name, value }) {
    state.new_zone[name] = value
  },
  [types.SET_NEW_ZONE] (state, zone) {
    state.new_zone = $.extend(true, {}, zone)
  },
  [types.SET_RECORDS] (state, result) {
    state.records = result.records
    state.total_records = result.total_records
  },
  [types.NEXT_ZONES_PAGE] (state) {
    state.zoneOffset += state.zoneLimit
  },
  [types.PREV_ZONES_PAGE] (state) {
    state.zoneOffset -= state.zoneLimit
    if (state.zoneOffset < 0) {
      state.zoneOffset = 0
    }
  },
  [types.TOGGLE_SHOW_ALL_ZONES] (state) {
    state.showAllZones = !state.showAllZones
    state.zoneOffset = 0
    state.zonePage = 0
    if (state.showAllZones) {
      state.zoneLimit = state.total_zones
    } else {
      state.zoneLimit = 10
    }
  },
  [types.FIRST_RECORDS_PAGE] (state) {
    state.recordsOffset = 0
    state.recordsPage = 0
  },
  [types.NEXT_RECORDS_PAGE] (state) {
    state.recordsOffset += state.recordsLimit
  },
  [types.PREV_RECORDS_PAGE] (state) {
    state.recordsOffset -= state.recordsLimit
    if (state.recordsOffset < 0) {
      state.recordsOffset = 0
    }
  },
  [types.TOGGLE_SHOW_ALL_RECORDS] (state) {
    state.showAllRecords = !state.showAllRecords
    state.recordsOffset = 0
    state.recordsPage = 0
    if (state.showAllRecords) {
      state.recordsLimit = state.total_records
    } else {
      state.recordsLimit = 10
    }
  },
  [types.RESET_DISABLED_ZONE_TIMERS] (state) {
    state.zone_timers_loaded = false
  },
  [types.ZONE_REFRESH_TIMERS_LOADED] (state) {
    state.zone_timers_loaded = true
    state.disabled_zones = []
  },
  [types.NEW_ZONE_ADDED] (state, zone) {
    zone.last_start = 60
    state.disabled_zones.push(zone)
  },
  [types.INCREASE_STARTUP_DISABLED_ZONE] (state, index) {
    let zone = state.disabled_zones[index]
    zone.last_start -= 1
    state.disabled_zones[index] = zone
  },
  [types.REMOVE_DISABLED_ZONE_BY_INDEX] (state, index) {
    state.disabled_zones.splice(index, 1)
  }
}

const actions = {
  /*
   * Get the list of zones from the server and put them in the state
   */
  getZones ({ state, commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/dns?limit=' + state.zoneLimit + '&offset=' + state.zoneOffset,
        success (result) {
          commit(types.GET_ZONES, result)
          if (result.total_zones === 0) {
            commit(types.NULL_ACTIVE_ZONE)
          } else {
            dispatch('getActiveZoneRecords')
          }
          resolve()
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  // Get and display the next page of zones
  nextZonesPage ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      commit(types.NEXT_ZONES_PAGE)
      dispatch('getZones').then((result) => {
        commit(types.FIRST_RECORDS_PAGE)
        commit(types.SET_DEFAULT_ACTIVE_ZONE)
      })
    })
  },
  // Get and display the previous page of zones
  prevZonesPage ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      commit(types.PREV_ZONES_PAGE)
      dispatch('getZones').then((result) => {
        commit(types.FIRST_RECORDS_PAGE)
        commit(types.SET_DEFAULT_ACTIVE_ZONE)
      })
    })
  },
  toggleShowAllZones ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      commit(types.TOGGLE_SHOW_ALL_ZONES)
      dispatch('getZones')
      resolve()
    })
  },
  /*
   * Find a zone based on the id
   */
  findZone ({ state }, { id }) {
    return new Promise((resolve, reject) => {
      let zone = state.zones.filter((zone) => {
        return zone.zone_id === id
      })[0]
      if (zone === 'undefined') {
        reject(zone)
      } else {
        resolve(zone)
      }
    })
  },
  /*
   * Gets the zone_aliases from the matching zone in state.zones
   * and puts them in editing_zone
   */
  refreshEditingZoneAliases ({ state, dispatch, commit }) {
    // Don't refresh if editing_zone is undefined
    if (state.editing_zone === 'undefined') {
      return
    }
    dispatch('findZone', { id: state.editing_zone.zone_id }).then((zone) => {
      commit(types.SET_EDITING_ZONE_ALIASES, { zoneAliases: zone.aliases })
    })
  },
  getPreviousRecordsPage ({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      commit(types.PREV_RECORDS_PAGE)
      dispatch('getActiveZoneRecords')
    })
  },
  getNextRecordsPage ({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      commit(types.NEXT_RECORDS_PAGE)
      dispatch('getActiveZoneRecords')
    })
  },

  toggleShowAllRecords ({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      commit(types.TOGGLE_SHOW_ALL_RECORDS)
      dispatch('getActiveZoneRecords')
      resolve()
    })
  },
  /*
   * Get the records of the currently selected zone from the server
   */
  getActiveZoneRecords ({ getters, state, commit }) {
    return new Promise((resolve, reject) => {
      if (typeof getters.active_zone === 'undefined' || Object.keys(getters.active_zone).length === 0) {
        // There is no active_zone and so set the records to nothing
        commit(types.SET_RECORDS, { records: [] })
        return resolve()
      }

      req.get({
        url: '/dns/' + getters.active_zone.zone_id + '/records?limit=' + state.recordsLimit + '&offset=' + state.recordsOffset,
        success (result) {
          commit(types.SET_RECORDS, result)
          resolve()
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  createDNSRecord ({ getters, dispatch, commit }, newRecord) {
    return new Promise((resolve, reject) => {
      // Add on trailing '.' if missing
      if (newRecord.record_type === 'MX' && newRecord.target !== '@' && newRecord.target.slice(-1) !== '.') {
        newRecord.target = newRecord.target + '.'
      }
      req.post({
        data: newRecord,
        notifications: false,
        modalNotifications: true,
        url: '/dns/' + getters.active_zone.zone_id + '/records/' + newRecord.record_type,
        success (result) {
          dispatch('getZones')
          resolve()
        },
        error (result) {
          commit(types.ADD_NOTIFICATION, {type: 'error', message: result.responseJSON.detail})
          reject()
        }
      })
    })
  },

  updateDNSRecord ({ dispatch, commit }, { zoneId, record }) {
    return new Promise((resolve, reject) => {
      // Copy the options into the record body
      for (var i in record.record_data) {
        record[i] = record.record_data[i]
      }
      // Add on trailing '.' if missing
      if (record.record_type === 'MX' && record.target.slice(-1) !== '.') {
        record.target = record.target + '.'
      }
      delete record['record_data']
      req.put({
        data: record,
        url: '/dns/' + zoneId + '/records/' + record.record_type + '/' + record.record_id,
        success (result) {
          // Replace the returned record with the current one
          dispatch('getActiveZoneRecords')
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  deleteDNSRecord ({ dispatch, commit }, { zoneId, record }) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/dns/' + zoneId + '/records/' + record.record_type + '/' + record.record_id,
        success (result) {
          dispatch('getActiveZoneRecords')
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  // eslint-disable-next-line camelcase
  createZone ({ commit, dispatch }, { zone_name, ttl }) {
    return new Promise((resolve, reject) => {
      let newZone = {
        zone_name: zone_name,
        ttl: parseInt(ttl)
      }
      req.post({
        data: newZone,
        url: '/dns',
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getZones')
          commit(types.NEW_ZONE_ADDED, result)
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  saveEditingZone ({ dispatch, commit, state }) {
    return new Promise((resolve, reject) => {
      req.put({
        data: state.editing_zone,
        url: '/dns/' + state.editing_zone.zone_id,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getZones')
          resolve()
        },
        error (result) {
          if (result.status === 422) {
            if (typeof result.responseJSON.msg === 'object') {
              reject(result.responseJSON.msg)
            }
          } else {
            commit(types.UNHANDLED_ERROR_NOTIFICATION)
            reject()
          }
        }
      })
    })
  },

  deleteZone ({ state, dispatch, commit }, zoneId) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/dns/' + zoneId,
        success (result) {
          dispatch('getZones').then(response => {
            if (typeof state.active_zone === 'undefined' || state.active_zone.id === zoneId) {
              commit(types.SET_DEFAULT_ACTIVE_ZONE)
            }
            resolve()
          }, response => {
            reject(response)
          })
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  deleteAlias ({ dispatch }, { zoneId, aliasId }) {
    return new Promise((resolve, reject) => {
      req.delete({
        url: '/dns/' + zoneId + '/aliases/' + aliasId,
        notifications: false,
        modalNotifications: true,
        success () {
          resolve()
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  // eslint-disable-next-line camelcase
  createNewAlias ({ dispatch }, { zoneId, alias_name }) {
    return new Promise((resolve, reject) => {
      let newAlias = {
        alias_name: alias_name
      }
      req.post({
        url: '/dns/' + zoneId + '/aliases',
        data: newAlias,
        notifications: false,
        modalNotifications: true,
        success (result) {
          dispatch('getZones').then(() => {
            dispatch('refreshEditingZoneAliases')
          })
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/dns.js